'use client';

import { c as _c } from "react/compiler-runtime";
import { activityLook, buildJobGroups, jobSummary, phaseLook, updateLabel, updateLook } from '@shared/all/helpers/runningJobs';
import { Bell, Layers } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useJobTerminal } from '../../hooks/useJobTerminal';
import { useRunners } from '../../hooks/useRunners';
import Indicator from './JobIndicator';
const ROW = 'flex w-full items-center gap-2 rounded px-1 py-[3px] text-left';

/**
 * Every live job across every board. Rendered inline — in the left rail on
 * wide screens, and on /spaces where the rail is hidden — so the fleet has one
 * home instead of a floating panel parked over the page.
 *
 * Each row is one line: which board, which card or job, and whether it is
 * working. Phase, mode, and runner live in the row's tooltip — with a dozen
 * jobs running, a third line each pushed the list off the screen.
 */
const RunningJobs = () => {
  const $ = _c(47);
  const {
    sessions,
    activities,
    runners,
    updateState
  } = useRunners();
  const {
    openJob,
    openJobKey
  } = useJobTerminal();
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = pathname?.match(/^\/s\/([^/?#]+)/);
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const m = t0;
  const currentSlug = m ? decodeURIComponent(m[1]) : null;
  const [notifyPermission, setNotifyPermission] = useState("unsupported");
  let t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof window === "undefined" || !("Notification" in window)) {
        return;
      }
      setNotifyPermission(Notification.permission);
    };
    t2 = [];
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== activities || $[5] !== currentSlug || $[6] !== notifyPermission || $[7] !== openJob || $[8] !== openJobKey || $[9] !== runners || $[10] !== sessions || $[11] !== updateState) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t10;
      if ($[19] !== updateState) {
        t10 = r => updateState[r.runnerId];
        $[19] = updateState;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      const updatingRunners = runners.filter(t10);
      const jobCount = sessions.length + activities.length;
      const showRunner = runners.length > 1;
      if (jobCount === 0 && runners.length === 0) {
        t9 = null;
        break bb0;
      }
      const groups = buildJobGroups(sessions, activities, currentSlug);
      t5 = "w-full min-w-0";
      let t11;
      if ($[21] !== jobCount || $[22] !== runners.length) {
        t11 = jobSummary(jobCount, runners.length);
        $[21] = jobCount;
        $[22] = runners.length;
        $[23] = t11;
      } else {
        t11 = $[23];
      }
      let t12;
      if ($[24] !== t11) {
        t12 = <span>{t11}</span>;
        $[24] = t11;
        $[25] = t12;
      } else {
        t12 = $[25];
      }
      let t13;
      if ($[26] !== notifyPermission) {
        t13 = notifyPermission === "default" && <button type="button" onClick={() => void Notification.requestPermission().then(setNotifyPermission)} title="Get a browser notification when a job finishes while this tab is in the background" className="flex shrink-0 items-center gap-1 rounded px-1 transition-colors hover:text-textPrimary"><Bell size={11} />Notify me</button>;
        $[26] = notifyPermission;
        $[27] = t13;
      } else {
        t13 = $[27];
      }
      if ($[28] !== t12 || $[29] !== t13) {
        t6 = <div className="flex items-center justify-between gap-2 px-1 text-[11px] text-textSecondary">{t12}{t13}</div>;
        $[28] = t12;
        $[29] = t13;
        $[30] = t6;
      } else {
        t6 = $[30];
      }
      t7 = jobCount === 0 && updatingRunners.length === 0 && <p className="px-1 pt-1.5 text-[12px] text-textSecondary/70">No jobs running.</p>;
      let t14;
      if ($[31] !== updateState) {
        t14 = r_0 => {
          const state = updateState[r_0.runnerId];
          const look = updateLook(state);
          return <div key={`update-${r_0.runnerId}`} title={look.label} className={ROW}><Indicator tone={look.tone} working={look.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{updateLabel(r_0.name, state)}</span></div>;
        };
        $[31] = updateState;
        $[32] = t14;
      } else {
        t14 = $[32];
      }
      t8 = updatingRunners.map(t14);
      t3 = "flex flex-col";
      let t15;
      if ($[33] !== currentSlug || $[34] !== openJob || $[35] !== openJobKey || $[36] !== showRunner) {
        t15 = group => <div key={group.key} className="min-w-0">{(() => {
            const header = <><Layers size={10} className="shrink-0 opacity-70" /><span className="min-w-0 truncate">{group.slug ?? "Unattributed"}</span>{group.slug === currentSlug && <span className="shrink-0 rounded bg-primary/15 px-1 text-[9px] font-semibold text-primary">HERE</span>}</>;
            return group.slug ? <Link href={`/s/${group.slug}`} title={`Open ${group.slug}`} className="flex items-center gap-1.5 px-1 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wider text-textSecondary rounded transition-colors hover:text-textPrimary">{header}</Link> : <div className="flex items-center gap-1.5 px-1 pb-0.5 pt-2 text-[10px] font-medium uppercase tracking-wider text-textSecondary">{header}</div>;
          })()}{group.activities.map(a => {
            const look_0 = activityLook(a.state);
            const row = <><Indicator tone={look_0.tone} working={look_0.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{a.label}</span>{showRunner && <span className="shrink-0 truncate text-[10px] text-textSecondary">{a.runnerName}</span>}</>;
            const title = [look_0.label, a.detail, a.runnerName].filter(Boolean).join(" \xB7 ");
            return a.spaceSlug ? <Link key={`${a.spaceSlug}::${a.id}`} href={`/s/${a.spaceSlug}`} title={title} className={`${ROW} transition-colors hover:bg-background-light`}>{row}</Link> : <div key={a.id} title={title} className={ROW}>{row}</div>;
          })}{group.sessions.map(s => {
            const look_1 = phaseLook(s.phase, s.agentState);
            const key = `${s.spaceSlug}::${s.postId}`;
            const openable = Boolean(s.spaceSlug);
            return <button key={key} type="button" disabled={!openable} onClick={() => openJob({
              spaceSlug: s.spaceSlug,
              postId: s.postId,
              title: s.taskTitle,
              mode: s.mode,
              runnerId: s.runnerId
            })} title={`${look_1.label} · ${s.mode} · ${s.runnerName}`} className={`${ROW} ${openJobKey === key ? "bg-background-light" : ""} ${openable ? "transition-colors hover:bg-background-light" : "cursor-default"}`}><Indicator tone={look_1.tone} working={look_1.working} /><span className="min-w-0 flex-1 truncate text-[12px] text-textPrimary">{s.taskTitle}</span>{showRunner && <span className="shrink-0 truncate text-[10px] text-textSecondary">{s.runnerName}</span>}</button>;
          })}</div>;
        $[33] = currentSlug;
        $[34] = openJob;
        $[35] = openJobKey;
        $[36] = showRunner;
        $[37] = t15;
      } else {
        t15 = $[37];
      }
      t4 = groups.map(t15);
    }
    $[4] = activities;
    $[5] = currentSlug;
    $[6] = notifyPermission;
    $[7] = openJob;
    $[8] = openJobKey;
    $[9] = runners;
    $[10] = sessions;
    $[11] = updateState;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t3 = $[12];
    t4 = $[13];
    t5 = $[14];
    t6 = $[15];
    t7 = $[16];
    t8 = $[17];
    t9 = $[18];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[38] !== t3 || $[39] !== t4) {
    t10 = <div className={t3}>{t4}</div>;
    $[38] = t3;
    $[39] = t4;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  let t11;
  if ($[41] !== t10 || $[42] !== t5 || $[43] !== t6 || $[44] !== t7 || $[45] !== t8) {
    t11 = <div className={t5}>{t6}{t7}{t8}{t10}</div>;
    $[41] = t10;
    $[42] = t5;
    $[43] = t6;
    $[44] = t7;
    $[45] = t8;
    $[46] = t11;
  } else {
    t11 = $[46];
  }
  return t11;
};
export default RunningJobs;